import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import './ManageContactGroupStyle.css'
import { InputField, Button } from '@components/common'
import { useAppSelector } from '@store/hooks'
import { getFullName } from '@utils/fullname.util'
import { IContact } from '@dataTypes/contact.type'

type AddMemberModalProps = {
  show: boolean
  onHide: () => void
  onConfirmAddMember: () => void
  preselectedMembers: IContact[]
  contactsToBeAdded: IContact[]
  setContactsToBeAdded: Dispatch<SetStateAction<IContact[]>>
  channel?: string
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  show,
  onHide,
  onConfirmAddMember,
  preselectedMembers,
  contactsToBeAdded,
  setContactsToBeAdded,
  channel,
}) => {
  const contacts = useAppSelector((state) => state.contacts.data)

  const [memberSelectionList, setMemberSelectionList] = useState<IContact[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)

  const handleCancel = (): void => {
    setContactsToBeAdded([])
    onHide()
  }

  useEffect(() => {
    if (contacts.length > 0) {
      let _members: IContact[] = contacts

      _members = _members.filter((c) =>
        getFullName(c, true).toLowerCase().includes(searchQuery.toLowerCase()),
      )

      if (channel) {
        _members = _members.filter((m) => {
          if (channel === '') return true
          if (channel === 'SMS' && m.cellphone) return true
          if (
            ['Messenger', 'Instagram'].includes(channel) &&
            m.patient?.meta_id
          )
            return true
          return false
        })
      }

      if (preselectedMembers.length > 0) {
        _members = _members.filter(
          (m) =>
            !preselectedMembers.some((pm) => pm.contact_id === m.contact_id),
        )
      }

      if (contactsToBeAdded.length > 0) {
        _members = _members.filter(
          (m) =>
            !contactsToBeAdded.some((ctba) => ctba.contact_id === m.contact_id),
        )
      }

      setMemberSelectionList(_members)
    } else setMemberSelectionList([])
  }, [searchQuery, contacts, contactsToBeAdded, preselectedMembers, channel])

  return (
    <Modal
      show={show}
      onHide={onHide}
      className="add-member-modal"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>Add member</Modal.Title>
      </Modal.Header>

      <Modal.Body className="custom-modal-body">
        <div className="amm-ap-container">
          <label>Add contacts</label>
          <div className="amm-member-list">
            {contactsToBeAdded.map((c, index) => (
              <div key={index} className="amm-ml-item">
                <span>{getFullName(c, true)}</span>
                <button
                  type="button"
                  className="unstyled-button tag-item-close ti ti-x"
                  aria-label="Remove contact"
                  onClick={() => {
                    setContactsToBeAdded(
                      contactsToBeAdded.filter(
                        (contact) => contact.contact_id !== c.contact_id,
                      ),
                    )
                  }}
                />
              </div>
            ))}
            <InputField
              value={searchQuery}
              onChange={(e) => {
                const _value = e.target.value
                setSearchQuery(e.target.value)
                if (_value.length > 0) setShowDropdown(true)
                else setShowDropdown(false)
              }}
              className="amm-input-field"
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setShowDropdown(false)}
            />
          </div>
          {showDropdown && memberSelectionList.length > 0 && (
            <div className="contacts-dropdown">
              <ul className="dropdown-list">
                {memberSelectionList.map((c) => (
                  <li
                    role="button"
                    tabIndex={0}
                    aria-label={getFullName(c, true)}
                    key={c.contact_id}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      setContactsToBeAdded([
                        ...contactsToBeAdded,
                        {
                          ...c,
                          contact_group_participant: {
                            date_time_created_at: new Date().toISOString(),
                          },
                        },
                      ])
                      setSearchQuery('')
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setContactsToBeAdded([
                          ...contactsToBeAdded,
                          {
                            ...c,
                            contact_group_participant: {
                              date_time_created_at: new Date().toISOString(),
                            },
                          },
                        ])
                        setSearchQuery('')
                      }
                    }}
                    className="dropdown-item"
                  >
                    <span> {getFullName(c, true)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="ms-auto">
          <Button
            size="md"
            className="button-secondary me-2"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            size="md"
            className="button-primary me-2"
            onClick={onConfirmAddMember}
          >
            Add selected
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default AddMemberModal
